// GuideRunner - K852: the host that makes Govern360 Guide actually run.
//
// Everything before this kit was inert: engine.js, highlight.js, resolve.js,
// the guide and the predicates were all on prod and nothing imported them, so
// Vite stripped them out and there was nothing to see. This is the piece that
// mounts them.
//
// WHAT OWNS WHAT
//   engine.js      step state, eligibility, completion, context, recovery
//   resolve.js     finding the control, and STOPPING when it is ambiguous
//   highlight.js   presentation only, text-only slots
//   predicates/    what "done" means for each step, read back from the server
//   this file      wiring, the live region, and the polling loop
//
// THE POLLING DECISION, which is the one thing here that is easy to get wrong
//   The engine counts a failed validate() as an ATTEMPT, and hands off after
//   two - deliberately, because agents loop and re-issuing the same
//   instruction a third time is the single most wasteful thing a guide can do.
//
//   So this does NOT call validate() on a timer. A 500ms poll would burn both
//   attempts within a second of the step opening and hand off before the user
//   had touched anything. Instead the predicate is polled DIRECTLY and
//   silently; validate() is called exactly twice at most per step:
//     * once when the predicate has gone true - it sees ok and advances
//     * once when the step's own timeout expires - it records the real
//       failure, pauses with the reason, and a retry produces the handoff
//   The attempt counter then means what it says: times the user was told this
//   had not worked.
//
// NO MODEL OUTPUT REACHES THE CARD. Every string shown comes from the guide
// JSON, the engine, or a predicate - never from the assistant's reply. The
// adapter has no HTML sink either (scripts/check-walkthrough-no-html.mjs
// fails the build if one appears), so spec section 6 holds structurally.

import { useEffect, useRef, useState, useCallback } from 'react';
import { X } from 'lucide-react';
import { useAuth } from '@govern360/shared';
import { GuideSession, STATE } from '../lib/walkthrough/engine.js';
import { createHighlighter } from '../lib/walkthrough/highlight.js';
import { resolveWhenReady, guidancePermitted, appBuild } from '../lib/walkthrough/resolve.js';
import { makeDeviceScopePredicates } from '../lib/walkthrough/predicates/device-scope.js';
import { listScopeRules } from '../lib/endpointApi.js';

const POLL_MS = 600;

/* G360_K852b - NAVIGATE FIRST, because you cannot point at a control on a page
   the user is not on.
   Without this the first step could only resolve if the user happened to be on
   the right route already, and from anywhere else it paused with a sentence -
   precisely the text-on-a-support-page experience this feature exists to
   replace. Opening a page is mechanical and reverses nothing; the spec lists
   nav.goto(route_id) as a permitted tool for exactly this.
   Only a ROUTE is navigated. Tabs are local state in this console, and the
   guide HIGHLIGHTS the tab for the user to click rather than clicking it for
   them - being shown where to click is the whole point. */
async function navigateIfNeeded(binding) {
  const want = binding && binding.navigate;
  if (!want) return;
  const target = '#' + String(want).replace(/^#/, '');
  const now = String(window.location.hash || '');
  // startsWith, not equality: a route can legitimately carry query params.
  if (now === target || now.startsWith(target + '?') || now.startsWith(target + '&')) return;
  window.location.hash = target;
  /* The hash change is synchronous; the render that follows is not.
     resolveWhenReady then waits on a MutationObserver for up to the step's
     timeout, so this pause only has to be long enough for React to START. */
  await new Promise((r) => setTimeout(r, 120));
}

/* The engine wants `permitted` on the resolution so it can use a sentence
   written for a reader. resolveNow does not set it, so it is attached here
   rather than letting the engine fall back to its own terser default. */
async function resolver(binding, opts) {
  await navigateIfNeeded(binding);
  const res = await resolveWhenReady(binding, opts);
  return { ...res, permitted: guidancePermitted(res) };
}

const TERMINAL = new Set([STATE.COMPLETED, STATE.FAILED, STATE.HANDED_OFF,
  STATE.CANCELLED, STATE.INVALIDATED]);

export default function GuideRunner({ guide, onEnd }) {
  const { tenant, user, activeRole } = useAuth();
  const [state, setState] = useState(null);
  const [say, setSay] = useState(null);
  const [announce, setAnnounce] = useState('');

  const sessionRef = useRef(null);
  const hlRef = useRef(null);
  const predsRef = useRef(null);
  const armedRef = useRef(null);      // step_id the save baseline was taken for
  const openedAtRef = useRef(0);      // when the current step became actionable

  /* feature_flags and entitlements are NOT wired: this console exposes no
     flag or entitlement object to read. They are left empty rather than
     filled with a guess, because diffContext compares them and a fabricated
     value would either invalidate constantly or never. Stated, not hidden. */
  const env = useCallback(() => ({
    tenant_id: (tenant && tenant.id) || null,
    user_id: (user && user.id) || null,
    role: activeRole || null,
    route_id: (window.location.hash || '').replace(/^#/, '') || null,
    resource_id: null,
    resource_version: null,
    app_build: appBuild(),
    feature_flags: {},
    entitlements: [],
  }), [tenant, user, activeRole]);

  const finish = useCallback((s) => {
    if (hlRef.current) hlRef.current.hide();
    setState(s);
  }, []);

  // --- start ------------------------------------------------------------
  useEffect(() => {
    if (!guide) return undefined;
    const e = env();
    predsRef.current = makeDeviceScopePredicates({
      tenantId: e.tenant_id, listScopeRules,
    });
    hlRef.current = createHighlighter();

    const s = new GuideSession({
      guide,
      env: e,
      resolver,
      predicates: predsRef.current,
      telemetry: () => {},   // K852: wired to the engine, not yet to a sink.
    });
    sessionRef.current = s;

    let cancelled = false;
    (async () => {
      if (!s.eligibility(e)) { if (!cancelled) { setSay(s.lastSay); setState(s.state); } return; }
      s.select();
      await s.advance();
      if (!cancelled) { setSay(s.lastSay); setState(s.state); openedAtRef.current = Date.now(); }
    })();

    return () => {
      cancelled = true;
      if (hlRef.current) { hlRef.current.destroy(); hlRef.current = null; }
      sessionRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guide]);

  // --- paint the current step -------------------------------------------
  useEffect(() => {
    const s = sessionRef.current;
    const hl = hlRef.current;
    if (!s || !hl) return;
    if (TERMINAL.has(s.state) || s.state === STATE.BLOCKED_BY_PREREQUISITE) { hl.hide(); return; }

    const step = s.step;
    if (!step) return;
    const res = s._lastResolution;
    const total = guide.steps.length;
    const n = s.stepIndex + 1;

    /* also_say carries the aside that is true but not a choice on this screen
       - the global Server rule. It is a separate paragraph on purpose: mixing
       it into the instruction is how a guide ends up describing three options
       where the UI offers two, which is exactly what v1 of this guide did. */
    const body = [step.say, step.also_say].filter(Boolean).join('\n\n');

    hl.show({
      element: (res && res.element) || null,
      title: guide.title,
      body: s.state === STATE.PAUSED ? (s.lastSay || body) : body,
      stepNumber: n, stepTotal: total,
      onDismiss: () => { s.cancel(); finish(s.state); if (onEnd) onEnd('cancelled'); },
      actions: [
        { label: 'Stop', describe: 'Stop the walkthrough',
          onClick: () => { s.cancel(); finish(s.state); if (onEnd) onEnd('cancelled'); } },
      ],
    });

    setAnnounce('Step ' + n + ' of ' + total + '. ' + (step.say || ''));
  }, [state, guide, finish, onEnd]);

  // --- the loop ---------------------------------------------------------
  useEffect(() => {
    const id = setInterval(async () => {
      const s = sessionRef.current;
      if (!s || TERMINAL.has(s.state)) return;

      // Context FIRST, before completion. A save that succeeded in the
      // previous tenant must never be credited to this one.
      const inv = s.checkContext(env());
      if (inv.invalidated) {
        setSay(inv.say); setAnnounce(inv.say); finish(s.state);
        if (onEnd) onEnd('invalidated');
        return;
      }

      if (s.state !== STATE.AWAITING_USER_ACTION && s.state !== STATE.PAUSED) return;

      const step = s.step;
      if (!step) return;
      const preds = predsRef.current;

      /* Arm the save baseline once the consequential step is open. Without
         it scope_rule_saved refuses outright rather than falling back to an
         existence check - a weaker check that still says "saved" is worse
         than an honest stop. */
      if (step.consequential && armedRef.current !== step.step_id && preds.armSave) {
        armedRef.current = step.step_id;
        await preds.armSave();
      }

      const pid = step.completion && step.completion.predicate_id;
      const pred = pid && preds[pid];
      if (!pred) return;   // the engine says so honestly on validate()

      let out;
      try { out = await pred(); } catch { out = { ok: false }; }
      const ok = out === true || (out && out.ok === true);

      if (ok) {
        // Silent poll went true: ONE validate(), which sees ok and advances.
        await s.validate();
        setSay(s.lastSay); setState(s.state);
        openedAtRef.current = Date.now();
        if (TERMINAL.has(s.state)) {
          finish(s.state);
          if (onEnd) onEnd(s.state === STATE.COMPLETED ? 'completed' : 'ended');
        }
        return;
      }

      // Not done yet. Only once the step's own timeout has passed does this
      // become a reported failure - see the polling note at the top.
      const budget = step.timeout_ms || 10000;
      if (s.state === STATE.AWAITING_USER_ACTION && Date.now() - openedAtRef.current > budget) {
        await s.validate();
        setSay(s.lastSay); setAnnounce(s.lastSay || '');
        setState(s.state);
        if (TERMINAL.has(s.state)) { finish(s.state); if (onEnd) onEnd('ended'); }
      }
    }, POLL_MS);
    return () => clearInterval(id);
  }, [env, finish, onEnd]);

  if (!guide) return null;

  /* ONE concise live region, for step-ready / blocked / failed. Spec section
     9: WCAG 4.1.3 covers progress, results and errors - not every
     conversational response - so there is no token-by-token narration here,
     and the adapter deliberately owns no live region of its own. */
  return (
    <>
      <div aria-live="polite" aria-atomic="true"
        style={{ position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)', whiteSpace: 'nowrap' }}>
        {announce}
      </div>
      {(TERMINAL.has(state) || state === STATE.BLOCKED_BY_PREREQUISITE) && (
        <div data-testid="guide-outcome"
          style={{ border: '1px solid var(--g-border)', background: 'var(--g-bg-soft)', borderRadius: 10, padding: '10px 12px', fontSize: 13 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ flex: 1, whiteSpace: 'pre-wrap' }}>
              {state === STATE.COMPLETED
                ? (guide.completion_statement || 'Done.')
                : (say || 'The walkthrough stopped.')}
            </span>
            <button type="button" aria-label="Dismiss" onClick={() => { setState(null); if (onEnd) onEnd('dismissed'); }}
              style={{ border: 0, background: 'none', cursor: 'pointer', color: 'var(--g-text-2)', padding: 0 }}>
              <X size={15} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
